import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  OnboardingState, 
  OnboardingStep, 
  OnboardingFormData, 
  GroupMemberData
} from '../types';
import { CustomerCategoryDTO } from "@/shared/api/model";

interface OnboardingStore extends OnboardingState {
  // Actions
  setSelectedCategory: (category: CustomerCategoryDTO) => void;
  setCurrentStep: (step: number) => void;
  setSteps: (steps: OnboardingStep[]) => void;
  updateFormData: (section: keyof OnboardingFormData, data: any) => void;
  updateFieldValue: (section: keyof OnboardingFormData, field: string, value: any) => void;
  setError: (field: string, error: string) => void;
  clearError: (field: string) => void;
  clearAllErrors: () => void;
  setLoading: (loading: boolean) => void;
  markStepCompleted: (stepIndex: number) => void;
  markStepIncomplete: (stepIndex: number) => void;
  addGroupMember: (member: GroupMemberData) => void;
  updateGroupMember: (index: number, member: GroupMemberData) => void;
  removeGroupMember: (index: number) => void;
  setGroupLeader: (memberIndex: number) => void;
  saveDraft: () => void;
  loadDraft: () => void;
  clearDraft: () => void;
  resetOnboarding: () => void;
  canProceedToStep: (stepIndex: number) => boolean;
  validateCurrentStep: () => boolean;
  getCompletionPercentage: () => number;
}

const initialFormData: OnboardingFormData = {
  customerData: {},
  custInfoData: {},
  locationData: [],
  documentData: [],
  productData: [],
  groupMembers: [],
  photoFileId: undefined,
  signatureFileId: undefined,
  photoUploadedAt: undefined,
  signatureUploadedAt: undefined,
};

const initialState: OnboardingState = {
  selectedCategoryId: undefined,
  selectedCategory: undefined,
  currentStep: 0,
  steps: [],
  formData: initialFormData,
  isLoading: false,
  errors: {},
  isDraft: false,
  lastSaved: undefined,
};

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSelectedCategory: (category: CustomerCategoryDTO) => {
        set({
          selectedCategory: category,
          selectedCategoryId: category.id,
          currentStep: 0,
          formData: initialFormData,
          errors: {},
        });
      },

      setCurrentStep: (step: number) => {
        const { steps } = get();
        if (step >= 0 && step < steps.length) {
          set({ currentStep: step });
        }
      },

      setSteps: (steps: OnboardingStep[]) => {
        set({ steps });
      },

      updateFormData: (section: keyof OnboardingFormData, data: any) => {
        set((state) => ({
          formData: {
            ...state.formData,
            [section]: data,
          },
          isDraft: true,
          lastSaved: new Date(),
        }));
      },

      updateFieldValue: (section: keyof OnboardingFormData, field: string, value: any) => {
        set((state) => ({
          formData: {
            ...state.formData,
            [section]: {
              ...(state.formData[section] as Record<string, any>),
              [field]: value,
            },
          },
          isDraft: true,
          lastSaved: new Date(),
        }));
      },

      setError: (field: string, error: string) => {
        set((state) => ({
          errors: {
            ...state.errors,
            [field]: error,
          },
        }));
      },

      clearError: (field: string) => {
        set((state) => {
          const { [field]: _, ...remainingErrors } = state.errors;
          return { errors: remainingErrors };
        });
      },

      clearAllErrors: () => {
        set({ errors: {} });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      markStepCompleted: (stepIndex: number) => {
        set((state) => ({
          steps: state.steps.map((step, index) =>
            index === stepIndex ? { ...step, isCompleted: true, validationErrors: [] } : step
          ),
        }));
      },

      markStepIncomplete: (stepIndex: number) => {
        set((state) => ({
          steps: state.steps.map((step, index) =>
            index === stepIndex ? { ...step, isCompleted: false } : step
          ),
        }));
      },

      addGroupMember: (member: GroupMemberData) => {
        set((state) => ({
          formData: {
            ...state.formData,
            groupMembers: [...(state.formData.groupMembers || []), member],
          },
          isDraft: true,
          lastSaved: new Date(),
        }));
      },

      updateGroupMember: (index: number, member: GroupMemberData) => {
        set((state) => ({
          formData: {
            ...state.formData,
            groupMembers: state.formData.groupMembers?.map((m, i) => (i === index ? member : m)) || [],
          },
          isDraft: true,
          lastSaved: new Date(),
        }));
      },

      removeGroupMember: (index: number) => {
        set((state) => ({
          formData: {
            ...state.formData,
            groupMembers: state.formData.groupMembers?.filter((_, i) => i !== index) || [],
          },
          isDraft: true,
          lastSaved: new Date(),
        }));
      },

      setGroupLeader: (memberIndex: number) => {
        set((state) => ({
          formData: {
            ...state.formData,
            groupMembers: state.formData.groupMembers?.map((member, index) => ({
              ...member,
              isLeader: index === memberIndex,
            })) || [],
          },
          isDraft: true,
          lastSaved: new Date(),
        }));
      },

      saveDraft: () => {
        set({ isDraft: true, lastSaved: new Date() });
      },

      loadDraft: () => {
        // Draft loading is handled by persist middleware
      },

      clearDraft: () => {
        set({ isDraft: false, lastSaved: undefined });
      },

      resetOnboarding: () => {
        set(initialState);
      },

      canProceedToStep: (stepIndex: number) => {
        const { steps } = get();
        if (stepIndex === 0) return true;
        
        // Check if all previous required steps are completed
        for (let i = 0; i < stepIndex; i++) {
          const step = steps[i];
          if (step.isRequired && !step.isCompleted) {
            return false;
          }
        }
        return true;
      },

      validateCurrentStep: () => {
        const { currentStep, steps } = get();
        const step = steps[currentStep];
        if (!step) return false;

        // Basic validation - can be extended based on step requirements
        return !step.isRequired || step.isCompleted;
      },

      getCompletionPercentage: () => {
        const { steps } = get();
        if (steps.length === 0) return 0;
        
        const completedSteps = steps.filter(step => step.isCompleted).length;
        return Math.round((completedSteps / steps.length) * 100);
      },
    }),
    {
      name: 'customer-onboarding-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedCategoryId: state.selectedCategoryId,
        // Don't persist selectedCategory and steps - they're too large and should be refetched
        currentStep: state.currentStep,
        // Only persist user-entered form data
        formData: {
          customerData: state.formData.customerData,
          custInfoData: state.formData.custInfoData,
          // Simplify location data - remove nested objects
          locationData: state.formData.locationData?.map(loc => ({
            locationConfigName: loc.locationConfigName,
            houseNumber: loc.houseNumber,
            postalCode: loc.postalCode,
            isPrimary: loc.isPrimary,
            addressType: loc.addressType,
            // Don't store full location selections - too large
          })),
          // Simplify document data
          documentData: state.formData.documentData?.map(doc => ({
            documentTypeCode: doc.documentTypeCode,
            documentTypeName: doc.documentTypeName,
            fileName: doc.fileName,
            fileId: doc.fileId,
            isRequired: doc.isRequired,
          })),
          // Simplify product data
          productData: state.formData.productData?.map(prod => ({
            productCode: prod.productCode,
            productName: prod.productName,
            productType: prod.productType,
          })),
          groupMembers: state.formData.groupMembers,
          photoFileId: state.formData.photoFileId,
          signatureFileId: state.formData.signatureFileId,
          photoUploadedAt: state.formData.photoUploadedAt,
          signatureUploadedAt: state.formData.signatureUploadedAt,
        },
        isDraft: state.isDraft,
        lastSaved: state.lastSaved,
      }),
    }
  )
);
